import type { SettingsResponse } from "../../shared/types";
import { flashsuitePages } from "../components/FlashsuiteMenu";
import { GroupedList, ListRow } from "../components/season/GroupedList";
import { Pill } from "../components/ui/Pill";
import { useAuth } from "../auth/AuthContext";
import { useSettingsData } from "../setup/useSettingsData";
import { type PaneKey, paneLine, paneStatus, SNAV, useKeys } from "./SettingsView";

/**
 * The six settings pages as rows (Brand, Sending email and the rest), each with its state, then the way to
 * Flashsuite for the account. `selected` is the one open beside the list on the iPad's side.
 */
export function SettingsList({ data, keys, selected, className }: { data: SettingsResponse; keys: ReturnType<typeof useKeys>; selected?: PaneKey; className?: string }) {
  const { flashsuite } = useAuth();
  const account = flashsuitePages(flashsuite)?.billing;
  return (
    <>
      <GroupedList className={className}>
        {SNAV.map((p) => {
          const [tone, label] = paneStatus(p.key, data, keys);
          return (
            <ListRow
              key={p.key}
              icon={p.icon}
              title={p.label}
              sub={paneLine(p.key, data, keys)}
              end={
                <Pill tone={tone} dot={tone !== "plain"}>
                  {label}
                </Pill>
              }
              on={selected === p.key}
              to={`/settings/${p.key}`}
            />
          );
        })}
      </GroupedList>
      {account ? (
        <GroupedList className="mo-account">
          <ListRow icon="building" title="Account, billing and team" sub="In Flashsuite" href={account} />
        </GroupedList>
      ) : null}
    </>
  );
}

/** The settings rows for a page that has not read the settings itself (More, on the iPad's side). */
export function SettingsRows() {
  const { data } = useSettingsData();
  const keys = useKeys();
  if (!data) return null;
  return <SettingsList data={data} keys={keys} />;
}
